/**
 * Pay Periods API
 *
 * Functions for interacting with the pay periods endpoints.
 */

import { api } from './client';
import type { PayPeriod, PayPeriodStatus, PayPeriodSummary } from '../types/models';

// =============================================================================
// Request/Response Types
// =============================================================================

export interface CreatePayPeriodInput {
  name: string;
  startDate: string;
  endDate: string;
}

export interface UpdatePayPeriodInput {
  name?: string;
  startDate?: string;
  endDate?: string;
}

export interface ListPayPeriodsParams {
  status?: PayPeriodStatus;
  page?: number;
  limit?: number;
}

export interface ListPayPeriodsResponse {
  payPeriods: PayPeriod[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PayPeriodDetailResponse {
  payPeriod: PayPeriod;
  summary: PayPeriodSummary;
}

// =============================================================================
// Pay Period CRUD
// =============================================================================

/**
 * List pay periods with optional filters
 */
export async function listPayPeriods(params?: ListPayPeriodsParams): Promise<ListPayPeriodsResponse> {
  const response = await api.get<ListPayPeriodsResponse>('/pay-periods', { params });
  return response.data;
}

/**
 * Get a single pay period by ID with summary stats
 */
export async function getPayPeriod(id: string): Promise<PayPeriodDetailResponse> {
  const response = await api.get<PayPeriodDetailResponse>(`/pay-periods/${id}`);
  return response.data;
}

/**
 * Create a new pay period
 */
export async function createPayPeriod(data: CreatePayPeriodInput): Promise<{ message: string; payPeriod: PayPeriod }> {
  const response = await api.post<{ message: string; payPeriod: PayPeriod }>('/pay-periods', data);
  return response.data;
}

/**
 * Update a pay period
 */
export async function updatePayPeriod(
  id: string,
  data: UpdatePayPeriodInput
): Promise<{ message: string; payPeriod: PayPeriod }> {
  const response = await api.put<{ message: string; payPeriod: PayPeriod }>(`/pay-periods/${id}`, data);
  return response.data;
}

/**
 * Close a pay period
 */
export async function closePayPeriod(id: string): Promise<{ message: string; payPeriod: PayPeriod }> {
  const response = await api.post<{ message: string; payPeriod: PayPeriod }>(`/pay-periods/${id}/close`);
  return response.data;
}

/**
 * Delete a pay period
 */
export async function deletePayPeriod(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/pay-periods/${id}`);
  return response.data;
}
